import { NavLink } from "react-router-dom";
import { Button, Switch, Tooltip } from "@heroui/react";
import { PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { cn } from "@/lib/utils";
import { NAV_ITEMS } from "./nav";
import { BrandMark } from "./BrandMark";
import { useDeskMode } from "@/lib/mode";

/**
 * Simple ⇄ Pro switch at the foot of the rail (Coinbase's "Advanced" toggle).
 * Pro = the full trading terminal; Simple = one plain answer for beginners.
 */
function ModeSwitch({ collapsed }: { collapsed: boolean }) {
	const [mode, setMode] = useDeskMode();
	const pro = mode === "pro";
	return (
		<div data-tour="mode" className={cn("border-t border-separator p-3", collapsed && "flex justify-center px-0")}>
			{collapsed ? (
				// A Switch inside Tooltip.Trigger loses its input (the trigger
				// swallows the press), so the collapsed rail uses a toggle button.
				<ModeToggle />
			) : (
				<>
					{/* The control must sit INSIDE Switch.Content (the <label>): outside it,
					    the text isn't clickable and the parts stack vertically. */}
					<Switch size="sm" isSelected={pro} onChange={on => setMode(on ? "pro" : "simple")}>
						<Switch.Content className="flex cursor-pointer items-center gap-2">
							<Switch.Control>
								<Switch.Thumb />
							</Switch.Control>
							<span className="text-[13px] font-medium">Pro mode</span>
						</Switch.Content>
					</Switch>
					{/* Fixed two-line height: if this text changed height on toggle, the
					    switch above it would jump and a second click would miss. */}
					<p className="mt-1 h-[2.75em] text-2xs leading-snug text-muted">
						{pro ? "On: charts, levels and the full checklist." : "Off: one clear answer. Turn on for charts and levels."}
					</p>
				</>
			)}
		</div>
	);
}

/** The Pro switch as one small button: the collapsed rail's, and the phone top bar's. */
export function ModeToggle({ tooltipPlacement = "right" }: { tooltipPlacement?: "right" | "bottom" }) {
	const [mode, setMode] = useDeskMode();
	const pro = mode === "pro";
	return (
		<Tooltip delay={0}>
			<Button
				size="sm"
				variant={pro ? "primary" : "ghost"}
				aria-pressed={pro}
				aria-label={`Pro mode ${pro ? "on" : "off"}`}
				onPress={() => setMode(pro ? "simple" : "pro")}
				className="h-7 min-w-0 px-2 text-2xs font-bold"
			>
				PRO
			</Button>
			<Tooltip.Content placement={tooltipPlacement}>{pro ? "Pro mode on: full terminal. Press for Simple." : "Simple mode. Press for Pro: charts and levels."}</Tooltip.Content>
		</Tooltip>
	);
}

interface NavRailProps {
	collapsed: boolean;
	/** False on narrow screens, where the rail is always icons-only. */
	canToggle?: boolean;
	onToggle: () => void;
}

/**
 * Left navigation rail (Kraken Pro / Coinbase Advanced pattern): icon + label
 * when expanded, icons with tooltips when collapsed.
 */
export function NavRail({ collapsed, canToggle = true, onToggle }: NavRailProps) {
	return (
		<aside
			className={cn(
				"fixed inset-y-0 left-0 z-40 flex flex-col border-r border-separator bg-background transition-[width] duration-200",
				collapsed ? "w-16" : "w-56",
			)}
		>
			<div className={cn("flex h-14 items-center gap-2.5 border-b border-separator", collapsed ? "justify-center" : "px-4")}>
				<BrandMark collapsed={collapsed} />
			</div>

			<nav aria-label="Main" className="flex flex-1 flex-col gap-0.5 p-2">
				{NAV_ITEMS.map((item) => {
					const link = (
						<NavLink
							key={item.path}
							to={item.path}
							end={item.path === "/"}
							aria-label={collapsed ? item.label : undefined}
							className={({ isActive }) =>
								cn(
									"flex h-9 items-center gap-3 rounded-lg text-[13px] font-medium outline-none transition-colors",
									"focus-visible:ring-2 focus-visible:ring-focus",
									collapsed ? "justify-center" : "px-3",
									isActive
										? "bg-default text-foreground"
										: "text-muted hover:bg-default hover:text-foreground",
								)
							}
						>
							<img src={item.icon} alt="" aria-hidden className="size-5 shrink-0" />
							{!collapsed && <span className="truncate">{item.label}</span>}
						</NavLink>
					);
					if (!collapsed) return link;
					return (
						<Tooltip key={item.path} delay={0}>
							<Tooltip.Trigger>{link}</Tooltip.Trigger>
							<Tooltip.Content placement="right">{item.label}</Tooltip.Content>
						</Tooltip>
					);
				})}
			</nav>

			<ModeSwitch collapsed={collapsed} />

			<div className={cn("border-t border-separator p-2", collapsed && "flex justify-center", !canToggle && "hidden")}>
				<Button
					variant="ghost"
					size="sm"
					isIconOnly={collapsed}
					fullWidth={!collapsed}
					aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
					onPress={onToggle}
					className={cn(!collapsed && "justify-start text-muted")}
				>
					{collapsed ? <PanelLeftOpen className="size-4" /> : <PanelLeftClose className="size-4" />}
					{!collapsed && "Collapse"}
				</Button>
			</div>
		</aside>
	);
}
